import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

/**
 * Fills the available viewport and keeps page shells height-bounded so
 * each screen can keep a static header and scroll only its content area.
 * Does not scroll the entire page as a document.
 */
export const MemberScrollShell = ({ children }: { children?: ReactNode }) => (
  <div
    role="region"
    aria-label="Member content"
    className="absolute inset-0 min-h-0 overflow-hidden"
    data-testid="memberScrollShell_root"
  >
    <div
      className={[
        'absolute inset-0 min-h-0 overflow-hidden',
        // Page fills the visible area (never taller than the device viewport).
        // Roots without an inner scrollport scroll here instead of being clipped.
        '[&>*]:!h-full [&>*]:!min-h-0 [&>*]:!max-h-full',
        '[&>*]:!overflow-y-auto [&>*]:!overflow-x-hidden [&>*]:overscroll-y-contain',
        // Pages that own their scrollport opt out of root scrolling
        '[&>[data-member-scroll=contain]]:!overflow-hidden',
        // Nested h-screen / min-h-screen roots (e.g. wrapped questionnaires) must fill, not 100vh
        '[&_.h-screen]:!h-full [&_.h-screen]:!max-h-full [&_.h-screen]:!min-h-0',
        '[&_.min-h-screen]:!min-h-0',
        // Common pattern: root > main > scroll
        '[&>*>main]:!min-h-0 [&>*>main]:!flex-1 [&>*>main]:!overflow-hidden',
        // Unlock flex scrollports so content scrolls under a static header.
        // Containment stays on the page-level scrollport above: applying it to
        // every nested scrollport broke scroll chaining, so a pane that had
        // reached its own end swallowed the gesture and the page could not be
        // scrolled back up.
        '[&_.overflow-y-auto]:!min-h-0',
        '[&_.overflow-y-auto]:[-webkit-overflow-scrolling:touch]',
      ].join(' ')}
      data-testid="memberScrollShell_viewport"
    >
      {children ?? <Outlet />}
    </div>
  </div>
);
